/** @format */

import { Subscription } from 'rxjs/internal/Subscription';

import { isPlatformBrowser } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostListener,
	Inject,
	input,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	SimpleChanges
} from '@angular/core';

import { BreakPoint, BreakPointNew } from '@shared/data-access/enums';
import { Store as NgRxStore } from '@ngrx/store';

import { chain } from '@core/chain';

import { selectSelectedColorAttribute } from '@pdp/attributes/data-access';
import {
	ImageFormat,
	Scene7MediaSetItem,
	Scene7MediaSetResponse,
	Scene7VideoSetItem
} from '@shared/media/data-access';
import { Environment, KillSwitches, ProcessedProduct } from '@shared/data-access/interfaces';
import { HmfMediaSetItem, HmfProductImageData } from '@dsg-tech/homefield-utils';
import { ProductImagePictureSource, ProductImageService } from '@pdp/utils';
import {
	DeviceService,
	EmitEvent,
	EventBusService,
	Events,
	IsBotService,
	Scene7Service,
	UtilityService,
	WINDOW
} from '@shared/data-access/services';
import { selectKillswitch, selectStyliticsWidgetShown } from '@pdp/data-access/state';

@Component({
	selector: 'pdp-product-image-viewer',
	templateUrl: './product-image-viewer.component.html',
	styleUrl: './product-image-viewer.component.scss',
	standalone: false
})
export class ProductImageViewerComponent implements OnChanges, OnInit, OnDestroy {
	@Input() available = true;

	@Input() catalogEntryTypeCode: string;

	@Input() eCode: string;

	@Input() productName: string;

	@Input() swatchColor = '';

	@Input() imageBadge: string;

	@Input() customizedUrls: string;

	@Input() simpleImageViewer: boolean;

	@Input() product: ProcessedProduct;

	@Input() shouldShowOutOfStockRecs = false;

	@Input() shouldHaveBorder = false;

	@Input() alternativeInStockRecsEnabled: boolean;

	@Input() exclusiveProductType: string;

	@Input() killSwitches: KillSwitches;

	lazyLoadImage = input(false);

	/**
	 * @description
	 * Turns alt images into smaller, thumbnail images in a single row, for use on PLA template.
	 */
	@Input() smallerAltImages = false;

	heroImage: HmfMediaSetItem;

	showHeroImage = false;

	altImageActiveIndex = 0;

	altImageIndexStart = 1;

	expanded = true;

	altImageIndexEnd;

	altImageMaxDisplay = 0;

	imageViewerDisabled = false;

	imageUnavailable = false;

	isMobile: boolean;
	/**
	 * @description
	 * The chain identifier abbreviation
	 */
	chainIdentifierAbbr = chain.chainIdentifierAbbr;

	imageBadgeFilePattern: string;

	mediaSetItemCount = 0;

	mediaSetItems: HmfMediaSetItem[];

	nearbyStoreCount = 0;

	doesCurrentlySetStoreHaveInventory: boolean;

	private fullScreenLightGalleryInstance;

	private mediaSetSuffix = '';

	private swatchColorPrefix = '';

	selectedAttributeSubscription$: Subscription;

	defaultImage =
		'https://images.dickssportinggoods.com/assets/icons/dsg/ImageUnavailablePDPSwatch.svg';

	syndigoHotspotsEnabled = false;

	isImageGalleryZoomEnabled = this.ngrxStore.selectSignal(
		selectKillswitch('isImageGalleryZoomEnabled')
	);

	isJumpLinkEnabled = this.ngrxStore.selectSignal(selectKillswitch('isJumpLinkEnabled'));

	styliticsWidgetShown = this.ngrxStore.selectSignal(selectStyliticsWidgetShown);

	previousScrollPosition: number;

	heroImageProductImageData: HmfProductImageData;

	constructor(
		private environment: Environment,
		private changeDetectorRef: ChangeDetectorRef,
		private deviceService: DeviceService,
		private eventBusService: EventBusService,
		private hostElement: ElementRef,
		@Inject(PLATFORM_ID) protected platformId: object,
		private scene7Service: Scene7Service,
		private utilityService: UtilityService,
		private ngrxStore: NgRxStore,
		private isBotService: IsBotService,
		@Inject(WINDOW) protected window: Window
	) {}

	ngOnInit(): void {
		this.onResize();

		if (this.customizedUrls) {
			this.setCustomizedUrls();
			return;
		}

		this.listenForAttributeSwatchColorChangesFromState();

		this.syndigoHotspotsEnabled = this.utilityService.checkKillSwitch(
			'syndigoHotspotsEnabled',
			this.killSwitches,
			this.chainIdentifierAbbr
		);

		// Set the hero image as the default product image while waiting for the media set
		this.setHeroImage();

		// Check the swatch color
		if (typeof this.swatchColor === 'string' && this.swatchColor.trim() !== '') {
			this.swatchColor = this.swatchColor.replace(/[/ -]/g, '_');
			this.swatchColorPrefix = '_';
		}

		// Check the type code to see if the suffix needs updated
		if (
			typeof this.catalogEntryTypeCode === 'string' &&
			this.catalogEntryTypeCode.toLowerCase() !== 'bundlebean'
		) {
			this.mediaSetSuffix = '_is';
		}
		// Use image collections if enabled

		this.getMediaSetScene7();

		if (this.exclusiveProductType) {
			this.imageBadge = this.exclusiveProductType;
		}
	}

	/**
	 * @description
	 * Angular lifecycle event
	 * NOTE: To test in dev mode manually, use this in console after selecting in-stock-component in the Elements panel:
	 * var inStockComp = ng.getComponent($0); inStockComp.selectedColorAttribute = 'Clay'; ng.markDirty(inStockComp);
	 */
	ngOnChanges(changes: SimpleChanges): void {
		if (this.customizedUrls) {
			this.setCustomizedUrls();
			return;
		}
		// Check to see if the swatch color changed
		if (
			typeof changes !== 'undefined' &&
			typeof changes.swatchColor !== 'undefined' &&
			changes.swatchColor.currentValue !== changes.swatchColor.previousValue &&
			!changes.swatchColor.firstChange
		) {
			// Set the active index to 0
			this.altImageActiveIndex = 0;

			// Set the media set count
			this.mediaSetItemCount = 0;

			// Clear the lightGallery instance
			this.fullScreenLightGalleryInstance = undefined;

			this.setHeroImage();

			// Check the swatch color
			if (typeof this.swatchColor === 'string' && this.swatchColor.trim() !== '') {
				this.swatchColorPrefix = '_';
			}

			// Check the type code to see if the suffix needs updated
			if (
				typeof this.catalogEntryTypeCode === 'string' &&
				this.catalogEntryTypeCode.toLowerCase() !== 'bundlebean'
			) {
				this.mediaSetSuffix = '_is';
			}
			this.setProductImageData();
		}
	}

	ngOnDestroy() {
		if (this.selectedAttributeSubscription$) {
			this.selectedAttributeSubscription$.unsubscribe();
		}
	}

	setProductImageData() {
		if (isPlatformBrowser(this.platformId)) {
			// Get the image set
			this.scene7Service
				.getProductImageSet$(
					this.eCode,
					this.swatchColor,
					this.swatchColorPrefix,
					this.mediaSetSuffix,
					0
				)
				.subscribe({
					next: (mediaSetResponse: Scene7MediaSetResponse) => {
						// Check to ensure there is a media set
						if (!mediaSetResponse?.item) {
							this.setDefaultHeroOnImageUnavailable();
						} else {
							// Parse the Scene7 MediaSet items

							this.parseSetScene7MediaSetItems(mediaSetResponse);
							this.setdisplayValues();

							// Run change detection to set the new values
							this.changeDetectorRef.detectChanges();

							// Ensure Product Image Unavailable does not show.
							this.imageUnavailable = false;
						}
					},
					error: () => {
						this.setDefaultHeroOnImageUnavailable();
					}
				});
		}
	}

	/**
	 * @description
	 * Set the hero image as the default product image while waiting for the media set
	 */
	setHeroImage() {
		this.heroImage = {
			src: this.scene7Service.getProductImageUrl(
				this.eCode,
				this.catalogEntryTypeCode,
				this.swatchColor,
				undefined,
				{ wid: '1400', fmt: 'pjpeg' },
				false
			),
			templateSrc: this.scene7Service.getProductImageUrl(
				this.eCode,
				this.catalogEntryTypeCode,
				this.swatchColor,
				undefined,
				{},
				false
			),
			type: 'image',
			index: 0
		};

		const heroImageDimensions =
			this.isImageGalleryZoomEnabled() && isPlatformBrowser(this.platformId)
				? ProductImageService.getHeroImageDimensionsForScene7()
				: '385';

		this.heroImageProductImageData = {
			imageUrl: this.heroImage.src,
			imgFormat: ImageFormat.PJPEG,
			isLCPImage: !this.lazyLoadImage(),
			isNgOptimizedImageSupported: true,
			sourceImg: this.heroImage,
			selectedSwatchColor: this.swatchColor,
			width: heroImageDimensions,
			height: heroImageDimensions
		};

		// This flag will be set to true if default image is needed due to image error
		// Flag will hide full screen icon, set cursor to default and remove shadow on hover
		// to indicate to a user that there are no interactions
		this.imageViewerDisabled = false;
	}

	listenForAttributeSwatchColorChangesFromState() {
		this.selectedAttributeSubscription$ = this.ngrxStore
			.select(selectSelectedColorAttribute)
			.subscribe((attributeSelected) => {
				if (attributeSelected && attributeSelected.name === 'Color') {
					this.swatchColor = attributeSelected.value.replace(/[/ -]/g, '_');
					// Set the active index to 0
					this.altImageActiveIndex = 0;

					// Set the media set count
					this.mediaSetItemCount = 0;

					// Clear the lightGallery instance
					this.fullScreenLightGalleryInstance = undefined;
					this.setHeroImage();
					// Check the swatch color
					if (typeof this.swatchColor === 'string' && this.swatchColor.trim() !== '') {
						this.swatchColorPrefix = '_';
					}

					// Check the type code to see if the suffix needs updated
					if (
						typeof this.catalogEntryTypeCode === 'string' &&
						this.catalogEntryTypeCode.toLowerCase() !== 'bundlebean'
					) {
						this.mediaSetSuffix = '_is';
					}
					this.setProductImageData();
				}
			});
	}

	onExpandAllClick() {
		this.expanded = !this.expanded;

		if (this.expanded) {
			// Set the alt image display max
			this.altImageMaxDisplay = this.getAltImageDisplayCount(this.mediaSetItemCount);

			// Set the alt image index end
			this.altImageIndexEnd = this.altImageMaxDisplay + this.altImageIndexStart;

			if (this.previousScrollPosition) {
				this.window.scroll({
					top: this.previousScrollPosition,
					behavior: 'auto'
				});
			}
		} else {
			//retain previous scroll position
			this.previousScrollPosition = this.window.scrollY;
			this.altImageIndexEnd = this.mediaSetItemCount;
		}

		this.changeDetectorRef.detectChanges();
	}

	/**
	 * @description
	 * Click listener for targets which initialize full screen mode
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	onFullScreenTargetClick(index: number) {
		// Do not initialize full screen mode if
		if (!this.imageViewerDisabled) {
			// Load the lightGallery stylesheets
			this.loadLightGalleryStyles();

			// Check to see if the Event was passed in
			// if (typeof $event !== 'undefined' && $event.key === ' ' || $event.key === 'Enter' || $event.key === 'Spacebar') {}

			// Get a reference to the native image element
			let nativeImageElement = this.hostElement.nativeElement.querySelector(
				`#image-viewer-hero-wrapper-${this.eCode} img`
			);

			if (!this.simpleImageViewer && !this.showHeroImage) {
				nativeImageElement = this.hostElement.nativeElement.querySelector(
					`#alt-image-viewer-wrapper-${this.eCode} img`
				);
			}

			// Ensure there is an image
			if (
				typeof nativeImageElement !== 'undefined' &&
				nativeImageElement !== null &&
				typeof this.fullScreenLightGalleryInstance === 'undefined'
			) {
				/**
				 * `any` is needed here because lightgallery options are untyped and the
				 * documentation for the options for this version of lightgallery no longer exists
				 */
				let lgOptions: any = {
					autoplay: false,
					autoplayControls: false,
					hash: false,
					share: false,
					speed: 300,
					swipeThreshold: 100,
					mousewheel: false,
					getCaptionFromTitleOrAlt: false,
					download: false,
					autoplayFirstVideo: false,
					actualSize: false,
					closable: false,
					index: index || 0,
					loop: true,
					dynamic: true,
					dynamicEl: this.mediaSetItems,
					hideBarsDelay: 600000
				};

				// Check the viewport width
				if (this.deviceService.getDeviceBreakPoint() > BreakPoint.SMALL) {
					// This is expensive! Only calling directly and forcing recalculate because it is on a click event and not page load or window resize!
					const viewportDimensions = this.deviceService.getDeviceViewportDimensions(true);

					lgOptions = {
						thumbnail: true,
						thumbWidth: viewportDimensions.width / 6,
						thumbContHeight: viewportDimensions.height / 5,
						thumbMargin: 10,
						...lgOptions
					};
				}

				(($) => {
					// Create the lightGallery Class instance
					this.fullScreenLightGalleryInstance =
						$(nativeImageElement).lightGallery(lgOptions);

					// Set draggable attr to false on viewed image
					// to prevent ghost image dragging on desktop
					this.fullScreenLightGalleryInstance.on('onAferAppendSlide.lg', () => {
						const currentImage = $('.lg-current img');
						currentImage.attr('draggable', false);
					});

					// Register the close Event
					this.fullScreenLightGalleryInstance.on('onCloseAfter.lg', () => {
						// Un-register the close Event
						this.fullScreenLightGalleryInstance.off('onCloseAfter.lg');

						// Un-register the after append slide Event
						this.fullScreenLightGalleryInstance.off('onAferAppendSlide.lg');

						// Un-register the after slide Event
						this.fullScreenLightGalleryInstance.off('onAfterSlide.lg');

						// Destroy the lightGallery jQuery instance
						this.fullScreenLightGalleryInstance.data('lightGallery').destroy(true);

						// Reset the lightGallery Class reference
						this.fullScreenLightGalleryInstance = undefined;
					});
				})(jQuery);
			}
		}
	}

	/**
	 * @description
	 * Accepts a number which is the count of images in a set and determines how many images should be displayed in
	 * the grid
	 */
	private getAltImageDisplayCount(mediaSetItemCount: number): number {
		return Math.min(mediaSetItemCount, 4);
	}

	/**
	 * @description
	 * Accepts a number which is the count of images in a set and determines how many images should be displayed in
	 * the grid
	 */
	private getAltImageDisplayMax(mediaSetItemCount: number): number {
		if (mediaSetItemCount < 3) {
			return 0;
		}
		if (mediaSetItemCount < 5) {
			return 2;
		}
		return 4;
	}

	/**
	 * @description
	 * Loads the lightGallery plugin stylesheets
	 */
	private loadLightGalleryStyles(): void {
		// Load the main lightGallery stylesheet
		this.utilityService.loadStyleSheet(
			'assets/plugins/lightgallery/css/lightgallery.min.css',
			'lg-main'
		);

		// Load the lightGallery transitions stylesheet
		this.utilityService.loadStyleSheet(
			'assets/plugins/lightgallery/css/lg-transitions.min.css',
			'lg-transitions'
		);
	}

	toggleStoreLayerSideNav() {
		this.eventBusService.emit(new EmitEvent(Events.ChangeStore));
	}

	/**
	 * @description
	 * get media set items using scene 7
	 */
	private getMediaSetScene7() {
		// Get the image set
		this.scene7Service
			.getProductImageSet$(
				this.eCode,
				this.swatchColor,
				this.swatchColorPrefix,
				this.mediaSetSuffix,
				0
			)
			.subscribe({
				next: (mediaSetResponse: Scene7MediaSetResponse) => {
					// Check to ensure there is a media set
					if (typeof mediaSetResponse?.item !== 'undefined') {
						// Parse the Scene7 MediaSet items

						this.parseSetScene7MediaSetItems(mediaSetResponse);
						this.setdisplayValues();
					} else {
						this.setDefaultHeroOnImageUnavailable();
					}
				},
				error: () => {
					this.setDefaultHeroOnImageUnavailable();
				}
			});
	}

	// helper function to  the images from  Scene7MediaSetResponse
	private parseSetScene7MediaSetItems(mediaSetResponse: Scene7MediaSetResponse): void {
		this.mediaSetItems = this.parseMediaSetItems(
			Array.isArray(mediaSetResponse.item) ? mediaSetResponse.item : [mediaSetResponse.item]
		);
		// debug media set items
		// Update the hero image with the first image in the media set
		this.heroImage = this.mediaSetItems[0];

		// Set the media set count
		this.mediaSetItemCount = this.mediaSetItems.length;

		this.showHeroImage = this.mediaSetItemCount === 1;

		// Set the alt image display max
	}

	private setdisplayValues() {
		this.shouldHaveBorder = this.showHeroImage;
		this.altImageIndexStart = 0;
		this.expanded = true;
		this.altImageMaxDisplay = !this.simpleImageViewer
			? this.getAltImageDisplayCount(this.mediaSetItemCount)
			: this.getAltImageDisplayMax(this.mediaSetItemCount);

		// Set the alt image index end
		this.altImageIndexEnd = this.altImageMaxDisplay + this.altImageIndexStart;
	}

	setDefaultHeroOnImageUnavailable(): void {
		this.mediaSetItems = [];
		this.heroImage = {
			poster: this.defaultImage,
			templateSrc: this.defaultImage
		};
		this.imageUnavailable = true;
	}

	/**
	 * @description
	 * Parsed a given Scene7 MediaSet item array and builds an object with properties to be used in the template
	 */
	private parseMediaSetItems(scene7MediaSetItems: Scene7MediaSetItem[]): HmfMediaSetItem[] {
		// Return data
		const returnMediaSetItems: HmfMediaSetItem[] = [];

		// Early exit if the Scene7 MediaSet items are empty
		if (Array.isArray(scene7MediaSetItems) && scene7MediaSetItems.length === 0) {
			return returnMediaSetItems;
		}

		// Iterate through the Scene7 MediaSet items
		for (let i = 0, length = scene7MediaSetItems.length; i < length; i += 1) {
			// Get a reference to the current index for convenience and to prevent multiple lookups
			const currentScene7MediaSetItem: Scene7MediaSetItem = scene7MediaSetItems[i];

			// The image file name in Scene7
			let scene7ImageName: string;

			// Check where to get the image name from.  ImageComingSoon will not have a swatch object and only an image object
			if (typeof currentScene7MediaSetItem.s !== 'undefined') {
				// Use the swatch object
				scene7ImageName = currentScene7MediaSetItem.s.n;
			} else {
				// use the image object
				scene7ImageName = currentScene7MediaSetItem.i.n;
			}

			// Build a new parsed media set item
			const mediaSetItem: HmfMediaSetItem = this.buildMediaSetItem(
				i,
				scene7ImageName,
				currentScene7MediaSetItem
			);

			// Add the media set item to the return array
			returnMediaSetItems.push(mediaSetItem);
		}

		// Return the data
		return returnMediaSetItems;
	}

	private buildMediaSetItem(
		i: number,
		scene7ImageName: string,
		currentScene7MediaSetItem: Scene7MediaSetItem
	) {
		// Image widths for the srcset
		const FULLSCREEN_IMAGE_WIDTHS = [685, 1000, 1500, 2000];

		const mediaSetItem: HmfMediaSetItem = {
			srcset: '',
			type: '',
			index: i
		};

		// Set the thumbnail and src properties
		mediaSetItem.thumb = `${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.imageDirectory}${scene7ImageName}?wid=250&hei=250&fmt=pjpeg`;
		mediaSetItem.templateSrc = `${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.imageDirectory}${scene7ImageName}`;

		// Determine which type of asset the current item is
		if (
			typeof currentScene7MediaSetItem.type === 'string' &&
			currentScene7MediaSetItem.type.toLowerCase() === 'video'
		) {
			// Set the poster
			mediaSetItem.poster = `${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.imageDirectory}${scene7ImageName}?wid=1400&fmt=pjpeg`;

			// Set the type
			mediaSetItem.type = 'video';

			// Set the video HTML
			// Set the track source to use the first video name
			const closedCaptionTag = `<track label="English" kind="captions" srclang="en-us" src="${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.closedCaption}${currentScene7MediaSetItem.i.n}_CC.vtt">`;

			mediaSetItem.html = `<video crossorigin="anonymous" class="lg-video-object lg-html5" controls preload="auto" autoplay="false">
										<source src="${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.videoDirectory}${currentScene7MediaSetItem.i.n}" type="video/mp4">${closedCaptionTag}Your browser does not support HTML5 video</video>`;
		} else if (
			typeof currentScene7MediaSetItem.type === 'string' &&
			currentScene7MediaSetItem.type.toLowerCase() === 'video_set'
		) {
			// Set the poster
			mediaSetItem.poster = `${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.imageDirectory}${scene7ImageName}?wid=1400&fmt=pjpeg`;

			const videos = [];

			// Check to ensure there are VideoDesc items
			if (
				Array.isArray(currentScene7MediaSetItem.set.item) &&
				currentScene7MediaSetItem.set.item.length > 0
			) {
				// Iterate through the VideoDesc items
				currentScene7MediaSetItem.set.item.forEach((videoDesc: Scene7VideoSetItem) => {
					// Add the video to the Array
					videos.push(
						`${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.videoDirectory}${videoDesc.i.n}`
					);
				});
			}

			// Set the type
			mediaSetItem.type = 'video';

			// Set the video HTML to the first video in the array
			// Set the track source to use the first video name
			const closedCaptionTag = `<track label="English" kind="captions" srclang="en-us" src="${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.closedCaption}${
				Array.isArray(currentScene7MediaSetItem.set.item)
					? currentScene7MediaSetItem.set.item[0].i.n
					: currentScene7MediaSetItem.set.item.i.n
			}_CC.vtt">`;

			mediaSetItem.html = `<video crossorigin="anonymous" class="lg-video-object lg-html5" controls preload="auto" autoplay="false"><source src="${videos[0]}" type="video/mp4">${closedCaptionTag}Your browser does not support HTML5 video</video>`;
		} else {
			/*
			 * Default to image / image set because they do not have type defined on the JSONP response
			 */
			// Iterate through the image sizes
			FULLSCREEN_IMAGE_WIDTHS.forEach((width) => {
				// Build the image URL
				const image = `${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.imageDirectory}${scene7ImageName}?wid=${width}&hei=${width}&fit=constrain&fmt=pjpeg`;

				// Add to the element srcset
				mediaSetItem.srcset += `${image} ${width}w, `;
			});

			// Set the type
			mediaSetItem.type = 'image';

			// Set the src
			mediaSetItem.src = `${this.environment.thirdParty.scene7.baseConfig.baseUrl}${this.environment.thirdParty.scene7.baseConfig.imageDirectory}${scene7ImageName}?wid=1400&hei=1400&fit=constrain&fmt=pjpeg`;
		}
		return mediaSetItem;
	}

	private setCustomizedUrls() {
		this.mediaSetItems = [];
		for (let i = 0; i < this.customizedUrls.length; ++i) {
			this.mediaSetItems.push({
				src: this.customizedUrls[0],
				templateSrc: this.customizedUrls[0],
				thumb: this.customizedUrls[0],
				type: 'image',
				index: i
			});
		}

		this.heroImage = {
			src: this.customizedUrls[0],
			templateSrc: this.customizedUrls[0],
			type: 'image',
			index: 0
		};

		this.mediaSetItemCount = this.mediaSetItems.length;
	}

	/**
	 * @description Posts to rumrunner when a missing product image is detected
	 */
	handleImageError(): void {
		if (isPlatformBrowser(this.platformId)) {
			//determine if user-agent is a bot
			const isBot = this.isBotService.isBot(window.navigator.userAgent);
			if (!isBot) {
				// removes styling of image viewer that would indicate further interactions to a user
				this.imageViewerDisabled = true;
				this.setDefaultHeroOnImageUnavailable();
			}
		}
	}

	// If an individual image in the media set errors, replace that image with the Image Unavailable image
	handleAltImageError(index: number) {
		// just templateSrc is used in carousel, everything else is needed for lightgallery
		this.mediaSetItems[index].src = this.defaultImage;
		this.mediaSetItems[index].srcset = this.defaultImage;
		this.mediaSetItems[index].templateSrc = this.defaultImage;
		this.mediaSetItems[index].thumb = this.defaultImage;
	}

	getMediaSetPictureSources(mediaSetItem: HmfMediaSetItem): ProductImagePictureSource[] {
		return ProductImageService.getProductImageSourceSet(
			mediaSetItem,
			this.isImageGalleryZoomEnabled()
		);
	}

	getHeroImagePictureSources(): ProductImagePictureSource[] {
		return ProductImageService.getProductImageSourceSet(
			this.heroImage,
			this.isImageGalleryZoomEnabled()
		);
	}

	/**
	 * @description
	 * determines if the device is mobile based on breakpoints when the window resizes
	 */
	@HostListener('window:resize', ['$event'])
	private onResize(): void {
		const breakpoint = this.deviceService.getDeviceBreakPointNew(true);
		this.isMobile =
			breakpoint === BreakPointNew.EXTRAEXTRASMALL ||
			breakpoint === BreakPointNew.EXTRASMALL ||
			breakpoint === BreakPointNew.SMALL;

		if (this.isMobile) {
			this.altImageIndexStart = 1;
		} else {
			this.altImageIndexStart = !this.simpleImageViewer ? 0 : 1;
		}
	}

	trackByFn(index: number, item: HmfMediaSetItem): string {
		return index.toString() + item.type;
	}
}
